"use client";

import { useCallback, useEffect, useState } from "react";
import {
  type Question,
  type QuestionnaireAnswers,
  QUESTIONS,
  QUESTIONNAIRE_ANSWERS_KEY,
  getNextQuestion,
  isQuestionnaireComplete,
} from "@/lib/checklist-questionnaire";

interface DecisionTreeQuestionnaireProps {
  userId: string | null;
  initialAnswers?: QuestionnaireAnswers;
  onComplete: (answers: QuestionnaireAnswers) => void;
}

export function DecisionTreeQuestionnaire({ userId, initialAnswers = {}, onComplete }: DecisionTreeQuestionnaireProps) {
  const [answers, setAnswers] = useState<QuestionnaireAnswers>(initialAnswers);
  const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
  const [questionHistory, setQuestionHistory] = useState<Question[]>([]);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [textInput, setTextInput] = useState("");

  // Initialize with first question
  useEffect(() => {
    if (!currentQuestion && questionHistory.length === 0) {
      // Find the first applicable question
      const firstQuestion = getNextQuestion(answers, -1);
      if (firstQuestion) {
        setCurrentQuestion(firstQuestion);
      }
    }
  }, [currentQuestion, questionHistory, answers]);

  // Save answers to localStorage
  useEffect(() => {
    if (typeof window !== "undefined" && Object.keys(answers).length > 0) {
      const key = `${QUESTIONNAIRE_ANSWERS_KEY}:${userId ?? "guest"}`;
      window.localStorage.setItem(key, JSON.stringify(answers));
    }
  }, [answers, userId]);

  const handleAnswer = useCallback(
    (value: string | boolean) => {
      if (!currentQuestion || isTransitioning) return;

      setIsTransitioning(true);

      const newAnswers = {
        ...answers,
        [currentQuestion.id]: value,
      };
      setAnswers(newAnswers);

      // Check if questionnaire is complete
      if (isQuestionnaireComplete(newAnswers)) {
        // Small delay for animation
        setTimeout(() => {
          onComplete(newAnswers);
        }, 300);
        return;
      }

      // Get next question
      const currentIndex = QUESTIONS.findIndex((q) => q.id === currentQuestion.id);
      const nextQuestion = getNextQuestion(newAnswers, currentIndex);

      // Add current to history and move to next
      setTimeout(() => {
        setQuestionHistory((prev) => [...prev, currentQuestion]);
        setCurrentQuestion(nextQuestion);
        setTextInput("");
        setIsTransitioning(false);
      }, 300);
    },
    [currentQuestion, answers, isTransitioning, onComplete],
  );

  const handleBack = useCallback(() => {
    if (questionHistory.length === 0 || isTransitioning) return;

    setIsTransitioning(true);

    const previousQuestion = questionHistory[questionHistory.length - 1];
    const newHistory = questionHistory.slice(0, -1);

    // Remove the answer for the current question and the previous one
    const newAnswers = { ...answers };
    if (currentQuestion) {
      delete newAnswers[currentQuestion.id];
    }
    delete newAnswers[previousQuestion.id];

    setTimeout(() => {
      setAnswers(newAnswers);
      setQuestionHistory(newHistory);
      setCurrentQuestion(previousQuestion);
      setTextInput("");
      setIsTransitioning(false);
    }, 200);
  }, [questionHistory, answers, currentQuestion, isTransitioning]);

  const handleTextSubmit = useCallback(() => {
    const trimmed = textInput.trim();
    if (trimmed) {
      handleAnswer(trimmed);
    }
  }, [textInput, handleAnswer]);

  // Calculate progress
  const answeredCount = questionHistory.length;
  const totalEstimate = QUESTIONS.length; // Approximate total
  const progress = Math.min((answeredCount / totalEstimate) * 100, 95);

  if (!currentQuestion) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl">
      {/* Progress bar */}
      <div className="mb-8">
        <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
          <span>Question {answeredCount + 1}</span>
          <span>{Math.round(progress)}% complete</span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-primary transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Question card */}
      <div
        className={`card-elevated p-8 transition-all duration-300 ${
          isTransitioning ? "scale-[0.98] opacity-50" : "scale-100 opacity-100"
        }`}
      >
        {/* Back button */}
        {questionHistory.length > 0 && (
          <button
            type="button"
            onClick={handleBack}
            disabled={isTransitioning}
            className="mb-6 flex items-center gap-1.5 text-sm text-muted-foreground transition hover:text-foreground disabled:opacity-50"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Go back
          </button>
        )}

        {/* Question */}
        <div className="mb-8 space-y-2">
          <h2 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">{currentQuestion.text}</h2>
          {currentQuestion.subtext && <p className="text-sm text-muted-foreground">{currentQuestion.subtext}</p>}
        </div>

        {/* Answer options */}
        <div className="space-y-3">
          {currentQuestion.type === "boolean" && (
            <>
              <button
                type="button"
                onClick={() => handleAnswer(true)}
                disabled={isTransitioning}
                className="flex w-full items-center gap-4 rounded-2xl border border-border bg-card px-5 py-4 text-left transition hover:border-primary hover:bg-secondary/50 focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-50"
              >
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border-2 border-primary">
                  <span className="h-3 w-3 rounded-full bg-primary opacity-0 transition group-hover:opacity-50" />
                </span>
                <span className="text-base font-medium">Yes</span>
              </button>
              <button
                type="button"
                onClick={() => handleAnswer(false)}
                disabled={isTransitioning}
                className="flex w-full items-center gap-4 rounded-2xl border border-border bg-card px-5 py-4 text-left transition hover:border-primary hover:bg-secondary/50 focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-50"
              >
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border-2 border-primary">
                  <span className="h-3 w-3 rounded-full bg-primary opacity-0 transition group-hover:opacity-50" />
                </span>
                <span className="text-base font-medium">No</span>
              </button>
            </>
          )}

          {currentQuestion.type === "single_choice" && currentQuestion.options && (
            <>
              {currentQuestion.options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleAnswer(option.value)}
                  disabled={isTransitioning}
                  className="flex w-full items-center gap-4 rounded-2xl border border-border bg-card px-5 py-4 text-left transition hover:border-primary hover:bg-secondary/50 focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-50"
                >
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border-2 border-primary" />
                  <span className="text-base font-medium">{option.label}</span>
                </button>
              ))}
            </>
          )}

          {currentQuestion.type === "text" && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleTextSubmit();
              }}
              className="space-y-4"
            >
              <input
                type="text"
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                placeholder="Enter your answer..."
                disabled={isTransitioning}
                className="w-full rounded-2xl border border-border bg-card px-5 py-4 text-base outline-none transition placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:opacity-50"
              />
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={!textInput.trim() || isTransitioning}
                  className="btn btn-primary flex-1 py-3"
                >
                  Continue
                </button>
                <button
                  type="button"
                  onClick={() => handleAnswer("")}
                  disabled={isTransitioning}
                  className="btn btn-outline py-3"
                >
                  Skip
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Skip hint */}
      {currentQuestion.type !== "text" && (
        <p className="mt-6 text-center text-xs text-muted-foreground">
          Take your time. Your answers help us create a personalized checklist.
        </p>
      )}
    </div>
  );
}
